
const apiBaseUrl = import.meta.env.VITE_API_URL

const clerkApiJwtTemplateRaw = import.meta.env.VITE_CLERK_API_JWT_TEMPLATE
const clerkApiJwtTemplate =
  typeof clerkApiJwtTemplateRaw === "string" && clerkApiJwtTemplateRaw.trim() !== ""
    ? clerkApiJwtTemplateRaw.trim()
    : undefined

/** Matches Clerk `useAuth().getToken` — optional args for JWT templates and cache bypass. */
export type ApiGetToken = (options?: {
  template?: string
  skipCache?: boolean
}) => Promise<string | null>

export class ApiError extends Error {
  readonly status: number
  readonly data?: unknown

  constructor(message: string, status: number, data?: unknown) {
    super(message)
    this.name = "ApiError"
    this.status = status
    this.data = data
  }
}

export function resolveApiUrl(path: string): string {
  if (!apiBaseUrl || typeof apiBaseUrl !== "string") {
    throw new Error("VITE_API_URL is not set (required for API requests)")
  }
  const base = apiBaseUrl.replace(/\/$/, "")
  const suffix = path.startsWith("/") ? path : `/${path}`
  return `${base}${suffix}`
}

/** Same origin as `resolveApiUrl` but ws(s):// — for the realtime chat/notifications socket. */
export function resolveWsUrl(path: string): string {
  return resolveApiUrl(path).replace(/^http/, "ws")
}

function parseFailedResponseMessage(data: unknown, status: number): string {
  let message = `Request failed: ${status}`
  if (typeof data === "object" && data !== null && "detail" in data) {
    const detail = (data as { detail: unknown }).detail
    if (typeof detail === "string") {
      message = detail
    } else if (
      Array.isArray(detail) &&
      detail.length > 0 &&
      typeof detail[0] === "object" &&
      detail[0] !== null &&
      "msg" in detail[0] &&
      typeof (detail[0] as { msg: unknown }).msg === "string"
    ) {
      message = (detail[0] as { msg: string }).msg
    }
  }
  return message
}

/** Unauthenticated JSON GET (e.g. public share links). */
export async function publicApiFetchJson<T>(path: string): Promise<T> {
  const res = await fetch(resolveApiUrl(path), {
    method: "GET",
    headers: { Accept: "application/json" },
  })

  const contentType = res.headers.get("content-type") ?? ""
  const data: unknown = contentType.includes("application/json")
    ? await res.json()
    : await res.text()

  if (!res.ok) {
    throw new ApiError(parseFailedResponseMessage(data, res.status), res.status, data)
  }

  return data as T
}

/** Same bearer Clerk uses in `apiFetch` (JWT template when `VITE_CLERK_API_JWT_TEMPLATE` is set). */
export async function getClerkTokenForApi(
  getToken: ApiGetToken,
  options?: { skipCache?: boolean },
): Promise<string | null> {
  const skipCache = options?.skipCache === true
  if (clerkApiJwtTemplate) {
    return getToken({ template: clerkApiJwtTemplate, ...(skipCache && { skipCache }) })
  }
  return skipCache ? getToken({ skipCache }) : getToken()
}

export async function apiFetch<T>(
  path: string,
  init: RequestInit & {
    getToken: ApiGetToken
  },
): Promise<T> {
  const { getToken, ...requestInit } = init
  const token = await getClerkTokenForApi(getToken)
  if (!token) {
    throw new ApiError("Missing auth token", 401)
  }
  const doFetch = (bearer: string): Promise<Response> =>
    fetch(resolveApiUrl(path), {
      ...requestInit,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${bearer}`,
        ...(requestInit.headers ?? {}),
      },
    })

  let res = await doFetch(token)

  // Clerk's in-memory token cache can serve an already-expired JWT (e.g. a device clock
  // running behind makes the cached token "look" valid client-side while the backend
  // correctly rejects it). Force-mint a fresh token and retry once. Safe for any method:
  // a 401 means auth failed before the handler ran, so nothing was processed.
  if (res.status === 401) {
    const freshToken = await getClerkTokenForApi(getToken, { skipCache: true })
    if (freshToken && freshToken !== token) {
      res = await doFetch(freshToken)
    }
  }


  // 204 No Content has no body — skip parsing entirely.
  if (res.status === 204) {
    // call was successful but there is no body
    return undefined as T
  }

  const contentType = res.headers.get("content-type") ?? ""
  const data: unknown = contentType.includes("application/json")
    ? await res.json()
    : await res.text()

  if (!res.ok) {
    throw new ApiError(parseFailedResponseMessage(data, res.status), res.status, data)
  }

  return data as T
}
